import React, { useEffect, useRef } from 'react';
import { format, register, render, cancel } from 'timeago.js';
import { useTranslation } from 'react-i18next';
import { TDate } from 'timeago.js/lib/interface';

import ar from 'timeago.js/lib/lang/ar';
import de from 'timeago.js/lib/lang/de';
import es from 'timeago.js/lib/lang/es';
import fr from 'timeago.js/lib/lang/fr';
import id_ID from 'timeago.js/lib/lang/id_ID';
import it from 'timeago.js/lib/lang/it';
import ja from 'timeago.js/lib/lang/ja';
import ko from 'timeago.js/lib/lang/ko';
import pt_BR from 'timeago.js/lib/lang/pt_BR';
import ru from 'timeago.js/lib/lang/ru';
import th from 'timeago.js/lib/lang/th';
import tr from 'timeago.js/lib/lang/tr';
import vi from 'timeago.js/lib/lang/vi';
import zh_CN from 'timeago.js/lib/lang/zh_CN';
import zh_TW from 'timeago.js/lib/lang/zh_TW';

// Register all supported locales once at module load time.
// English is built-in to timeago.js and does not need registration.
register('ar', ar);
register('de', de);
register('es', es);
register('fr', fr);
register('id', id_ID);
register('it', it);
register('ja', ja);
register('ko', ko);
register('pt', pt_BR);
register('ru', ru);
register('th', th);
register('tr', tr);
register('vi', vi);
register('zh', zh_CN);
register('zh-TW', zh_TW);

/** Registered timeago locale codes (excluding built-in 'en'). */
const TIMEAGO_LOCALES = ['ar', 'de', 'es', 'fr', 'id', 'it', 'ja', 'ko', 'pt', 'ru', 'th', 'tr', 'vi', 'zh', 'zh-TW'];

/**
 * Normalize an i18next language value (which may be a regional variant like
 * `en-US`, `zh-CN`, `pt-BR`) to a registered timeago.js locale code.
 *
 * `zh-TW` is preserved as-is because it is registered distinctly from `zh`.
 * All other regional variants are mapped to their base language short code.
 * Falls back to 'en' (timeago.js built-in) for unrecognized codes.
 */
const normalizeLocale = (language: string): string => {
  // Exact match first (handles 'zh-TW' and simple codes)
  if (language === 'en' || TIMEAGO_LOCALES.includes(language)) {
    return language;
  }
  // Try matching by prefix (e.g. 'pt-BR' -> 'pt', 'zh-CN' -> 'zh')
  const base = TIMEAGO_LOCALES.find((code) => language.startsWith(code));
  return base || 'en';
};

interface LocalizedTimeAgoProps {
  datetime: TDate;
  live?: boolean;
  className?: string;
}

/**
 * A locale-aware wrapper around timeago.js that automatically uses
 * the current i18next language. All supported timeago.js locales are
 * registered at import time so consumers don't need to manage registration.
 *
 * Uses timeago.js `render()` for live-updating and `format()` for the
 * initial render, bypassing the timeago-react class component entirely.
 */
const LocalizedTimeAgo: React.FC<LocalizedTimeAgoProps> = ({ datetime, live = true, className }) => {
  const { i18n } = useTranslation();
  const timeRef = useRef<HTMLTimeElement>(null);

  const locale = normalizeLocale(i18n.language);
  const datetimeAttr = datetime instanceof Date ? datetime.getTime().toString() : String(datetime);

  useEffect(() => {
    const node = timeRef.current;
    if (!node) return;

    cancel(node);

    if (live) {
      node.setAttribute('datetime', datetimeAttr);
      render(node, locale);
    }

    return () => cancel(node);
  }, [datetime, locale, live, datetimeAttr]);

  return (
    <time ref={timeRef} className={className} dateTime={datetimeAttr}>
      {format(datetime, locale)}
    </time>
  );
};

export default LocalizedTimeAgo;
